import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  Table,
  Label,
  Row,
} from "reactstrap";
import classnames from "classnames";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Label as YLabel,
} from "recharts";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios";
import MonthReport from "./MonthReport";
import DayReport from "./DayReport";
import DateRangeReport from "./DateRange";
import WeekReport from "./WeekReport";

/* ---------------- COMPONENT ---------------- */

const ProductionReport = () => {
  const [machines, setMachines] = useState([]);
  const [parts, setParts] = useState([]);
  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [rangeData, setRangeData] = useState([]);
  const [rangeParts, setRangeParts] = useState([]);

  const machineId = localStorage.getItem("selectedMachineId") || "74";

  const [selectedPart, setSelectedPart] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const selectedMachineId = localStorage.getItem('selectedMachineId');
   const selectedCompanyId = localStorage.getItem('companyid');
  const [selectedMachine, setSelectedMachine] = useState(selectedMachineId);
  const [activeTab, setActiveTab] = useState("DAY");
  const [qualityTab, setQualityTab] = useState("ALL");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const [monthData, setMonthData] = useState([]);

  const [dayShiftData, setDayShiftData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // const PART_COLORS = [
  //   "#5ca56d", // green
  //   "#317a58", // blue
  //   "#285e34", // orange
  //   "#137534", // pink
  //   "#175f2e", // teal
  //   "#436b46", // violet
  // ];
  // const getPartColor = (index) =>
  //   PART_COLORS[index % PART_COLORS.length];

  useEffect(() => {
    fetchMachine();
  }, []);
  const fetchMachine = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`https://api.hanoch.in/api/machines/by-company/?id=${selectedCompanyId}`, {
        // headers: {
        //   "Content-Type": "application/json",
        //   Authorization: `Bearer ${authToken}`, 
        // },
      });

      if (response.ok) {
        const data = await response.json();
        setMachines(data?.data);
      } else if (response.status === 401) {

        localStorage.removeItem("authUser");
        localStorage.removeItem("admin");
      } else {
        console.error("Error fetching data:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- FETCH PARTS ---------------- */

  useEffect(() => {
    if (!selectedMachine) return;

    axios
      .get(`https://api.hanoch.in/api/mcc-parts/?machine_id=${selectedMachine}`)
      .then((res) => setParts(res.data || []))
      .catch((err) => console.error(err));
  }, [selectedMachine]);

  /* ---------------- FETCH DAY PRODUCTION ---------------- */
  const handleMachineChange = (event) => {
    const machineId = event.target.value;
    setSelectedMachine(machineId);
    localStorage.setItem('selectedMachineId', machineId);
  };

  useEffect(() => {
    if (selectedMachine && selectedPart && selectedDate) {
      fetchDayProduction();
    }
  }, [selectedPart]);

  const fetchDayProduction = async () => {
    if (!selectedMachine || !selectedDate) return;

    try {
      setIsLoading(true);

      const res = await axios.get(
        "https://api.hanoch.in/api/mcc-day-production/",
        {
          params: {
            machine_id: selectedMachine,
            partname: selectedPart || null, // ✅ IMPORTANT
            date: selectedDate,
          },
        }
      );

      const apiData = res.data || {};

      const transformed = Object.entries(apiData).map(
        ([shift, values]) => ({
          shift,
          good: values.good ?? 0,
          bad: values.bad ?? 0,
        })
      );

      setDayShiftData(transformed);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
const fetchWeekProduction = async () => {
  if (!selectedMachine || !selectedWeek) return;

  const { startDate, endDate } = getWeekDates(selectedWeek);

  try {
    setIsLoading(true);

    const res = await axios.get(
      "https://api.hanoch.in/api/mcc-production-graph-daterange/",
      {
        params: {
          machine_id: selectedMachine,
          start_date: startDate,
          end_date: endDate,
          partname: selectedPart || null,
        },
      }
    );

    // handle response same as DATE RANGE
  } catch (err) {
    console.error(err);
  } finally {
    setIsLoading(false);
  }
};
const [selectedWeek, setSelectedWeek] = useState(() => {
  const now = new Date();
  const year = now.getFullYear();

  const oneJan = new Date(year, 0, 1);
  const week = Math.ceil(
    (((now - oneJan) / 86400000) + oneJan.getDay() + 1) / 7
  );

  return `${year}-W${String(week).padStart(2, "0")}`;
});

const getWeekDates = (weekValue) => {
  const [year, week] = weekValue.split("-W");
  const firstDayOfYear = new Date(year, 0, 1);
  const daysOffset = (week - 1) * 7;

  const weekStart = new Date(firstDayOfYear);
  weekStart.setDate(firstDayOfYear.getDate() + daysOffset);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return {
    startDate: weekStart.toISOString().split("T")[0],
    endDate: weekEnd.toISOString().split("T")[0],
  };
};

  const fetchMonthlyProduction = async () => {
    if (!selectedMachine || !selectedPart || !selectedMonth) return;

    try {
      setIsLoading(true);

      const [year, month] = selectedMonth.split("-");

      const res = await axios.get(
        "https://api.hanoch.in/api/mcc-monthly-production/",
        {
          params: {
            machine_id: selectedMachine,
            partname: selectedPart,
            year,
            month: Number(month),
          },
        }
      );

      const apiData = res.data || {};

      // Expected format: { "1": {good: x, bad: y}, "2": {...} }
      const transformed = Object.entries(apiData).map(
        ([day, values]) => ({
          day,
          good: values.good ?? 0,
          bad: values.bad ?? 0,
        })
      );

      setMonthData(transformed);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (activeTab === "MONTH") {
      fetchMonthlyProduction();
    }
  }, [activeTab, selectedMachine, selectedPart, selectedMonth]);


  const fetchDateRangeProduction = async () => {
    if (!selectedMachine || !fromDate || !toDate) return;

    try {
      setIsLoading(true);

      const res = await axios.get(
        "https://api.hanoch.in/api/mcc-production-graph-daterange/",
        {
          params: {
            machine_id: selectedMachine,
            start_date: fromDate,
            end_date: toDate,
          },
        }
      );

      const parts = res.data?.parts || [];
      setRangeParts(parts);
      const dateMap = {};

      parts.forEach((part) => {
        if (selectedPart && part.part_number !== selectedPart) return;

        part.series.forEach((d) => {
          if (!dateMap[d.date]) {
            dateMap[d.date] = { date: d.date };
          }

          dateMap[d.date][`${part.part_number}_good`] = d.good || 0;
          dateMap[d.date][`${part.part_number}_bad`] = d.bad || 0;
        });
      });

      setRangeData(
        Object.values(dateMap).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        )
      );
    } catch (err) {
      console.error(err);
      setRangeData([]);
    } finally {
      setIsLoading(false);
    }
  };



  // useEffect(() => {
  //   if (parts.length > 0 && !selectedPart) {
  //     setSelectedPart(parts[0].part_name);
  //   }
  // }, [parts]);


  /* ---------------- UI ---------------- */
  const CustomDatePartTick = ({ x, y, payload }) => {
    const { date, partLabel } = payload;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="middle"
          fill="#ccc"
          fontSize={11}
        >
          {date}
        </text>
        <text
          x={0}
          y={0}
          dy={26}
          textAnchor="middle"
          fill="#888"
          fontSize={10}
        >
          {partLabel}
        </text>
      </g>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="OptiMize" breadcrumbItem="Production Report" />

          <Card>
            <CardHeader>


              {/* PERIOD TABS */}
              <Nav tabs>
                {["DAY", "WEEK", "MONTH", "DATE RANGE"].map((tab) => (
                  <NavItem key={tab}>
                    <NavLink
                      className={classnames({ active: activeTab === tab })}
                      onClick={() => setActiveTab(tab)}
                      style={{ cursor: "pointer" }}
                    >
                      By {tab}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </CardHeader>

            <CardBody>
              <Row className="mb-4 align-items-end">
                <Col md={2}>
                  <Label>Select Machine</Label>
                  <Input type="select" value={selectedMachine} onChange={handleMachineChange}>
                    <option value="">Select Machine</option>
                    {machines.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </Input>
                </Col>

                {/* DATE / MONTH / RANGE */}
                {activeTab === "DATE RANGE" ? (
                  <>
                    <Col md={2}>
                      <Label>From Date</Label>
                      <Input
                        type="date"
                        value={fromDate}
                        max={toDate || undefined}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </Col>

                    <Col md={2}>
                      <Label>To Date</Label>
                      <Input
                        type="date"
                        value={toDate}
                        min={fromDate || undefined}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </Col>
                  </>
                ) : activeTab === "MONTH" ? (
                  <Col md={3}>
                    <Label>Month</Label>
                    <Input
                      type="month"
                      value={selectedMonth}
                      max={new Date().toISOString().slice(0, 7)}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                  </Col>
                ) : activeTab === "WEEK" ? (
  <Col md={3}>
    <Label>Week</Label>
    <Input
      type="week"
      value={selectedWeek}
      onChange={(e) => setSelectedWeek(e.target.value)}
    />
  </Col>
) :  (
                  <Col md={3}>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={selectedDate}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </Col>
                )}

                <Col md={3}>
                  <Label>Part</Label>
                  <Input
                    type="select"
                    value={selectedPart}
                    onChange={(e) => setSelectedPart(e.target.value)}
                  >
                    <option value="">All Parts</option>
                    {parts.map((p) => (
                      <option key={p.id} value={p.part_name}>
                        {p.part_name}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md={3}>
                  <Button
                    color="primary"
                    className="w-100"
                    disabled={isLoading}
                    onClick={() => {
                      if (activeTab === "DAY") fetchDayProduction();
                        if (activeTab === "WEEK") fetchWeekProduction();
                      if (activeTab === "MONTH") fetchMonthlyProduction();
                      if (activeTab === "DATE RANGE") fetchDateRangeProduction();
                    }}
                  >
                    {isLoading ? <Spinner size="sm" /> : "Submit"}
                  </Button>

                </Col>
              </Row>

              {/* TAB CONTENT */}
              {activeTab === "DAY" && (
                <DayReport
                  machine={selectedMachine}
                  part={selectedPart}
                  date={selectedDate}
                  qualityTab={qualityTab}
                  setQualityTab={setQualityTab}
                />
              )}

              {activeTab === "MONTH" && (
                <MonthReport
                  machine={selectedMachine}
                  part={selectedPart}
                  month={selectedMonth}
                  qualityTab={qualityTab}
                  setQualityTab={setQualityTab}
                />
              )}

              {activeTab === "DATE RANGE" && (
                <DateRangeReport
                  selectedMachine={selectedMachine}
                  selectedPart={selectedPart}
                  fromDate={fromDate}
                  toDate={toDate}
                  qualityTab={qualityTab}
                  setQualityTab={setQualityTab}
                />
              )}
                {activeTab === "WEEK" && (
              <WeekReport
  selectedMachine={selectedMachine}
  selectedPart={selectedPart}
 week={selectedWeek}
 selectedWeek={selectedWeek}
  qualityTab={qualityTab}
  setQualityTab={setQualityTab}
/>

              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProductionReport;
